import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import { Logout, Save } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { MediaManagement } from './components/MediaManagement';

interface AboutUsData {
  title: string;
  videoUrl: string;
  description: string;
}



interface AboutData {
  aboutUs: AboutUsData;
  ourValues: {
    imageUrl: string;
  };
  organization: {
    title: string;
    description: string;
    imageUrl: string;
  };
}

export const AboutEditor: React.FC = () => {
  const { logout } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [aboutData, setAboutData] = useState<AboutData>({
    aboutUs: {
      title: '',
      videoUrl: '',
      description: '',
    },
    ourValues: {
      imageUrl: '',
    },
    organization: {
      title: '',
      description: '',
      imageUrl: '',
    },
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // 임시 업로드 관리
  const [tempUploads, setTempUploads] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    // 서버에서 About 데이터 불러오기
    const loadAboutData = async () => {
      try {
        const { aboutApi } = await import('../services/api');
        const aboutData = await aboutApi.getAboutData();
        setAboutData(aboutData);
      } catch (error) {
        console.error('About 데이터 로드 실패:', error);
      } finally {
        setLoading(false);
      }
    };
    loadAboutData();
  }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      const { aboutApi } = await import('../services/api');
      await aboutApi.saveAboutData(aboutData);
      setShowSuccess(true);
      
      // 저장 완료 - 임시 업로드는 유지 (파일 삭제하지 않음)
    } catch (error) {
      console.error('About 데이터 저장 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  // 임시 업로드 처리
  const handleTempUpload = (media: any) => {
    // 어떤 필드가 업데이트되었는지 확인 (Our Values 또는 Organization)
    const field = tabValue === 1 ? 'ourValues' : 'organization';
    setTempUploads(prev => ({ ...prev, [field]: media.url }));
  };

  // 임시 업로드 취소 처리
  const handleCancelUpload = async (mediaId: string) => {
    // 임시 업로드에서 제거
    const newTempUploads = { ...tempUploads };
    delete newTempUploads[mediaId];
    setTempUploads(newTempUploads);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('로그아웃 실패:', error);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          About 데이터를 불러오는 중...
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            About 관리
          </Typography>
          <Button
            color="inherit"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={loading}
          >
            저장
          </Button>
          <IconButton
            color="inherit"
            onClick={handleLogout}
            sx={{ ml: 1 }}
          >
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          About 페이지 관리
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="About Us" />
            <Tab label="Our Values" />
            <Tab label="Organization" />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <Box>
            <Typography variant="h5" gutterBottom>
              About Us 섹션
            </Typography>
            <MediaManagement
              onTempUpload={handleTempUpload}
              onCancelUpload={handleCancelUpload}
            />
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            <Typography variant="h5" gutterBottom>
              Our Values 섹션
            </Typography>
            <MediaManagement
              onTempUpload={handleTempUpload}
              onCancelUpload={handleCancelUpload}
            />
          </Box>
        )}

        {tabValue === 2 && (
          <Box>
            <Typography variant="h5" gutterBottom>
              Organization 섹션
            </Typography>
            <MediaManagement
              onTempUpload={handleTempUpload}
              onCancelUpload={handleCancelUpload}
            />
          </Box>
        )}

        <Snackbar
          open={showSuccess}
          autoHideDuration={6000}
          onClose={() => setShowSuccess(false)}
        >
          <Alert onClose={() => setShowSuccess(false)} severity="success">
            About 데이터가 성공적으로 저장되었습니다!
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}; 